/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable camelcase */
import React, { useState } from "react";
import { ArrowRightShape, JobSearchHit, JobSearchLocation } from "@themuse/design-system";
import { triggerSnowplowClickEvent } from "@themuse/snowplow-js-client";
import { UserInteractionActionTypes } from "interfaces/context/searchContextInterfaces";
import { genJobTrackingProps, TrackedElement } from "utils/tracking";
import { useSearchState, useSearchDispatch } from "hooks/SearchContext";
import styles from "./JobTileMinimal.module.scss";

export interface GetJobTileLocationProps {
    inputDMA: string;
    remote_work_location_type: string;
    locations: JobSearchLocation[];
    userLocationDma: string;
}

/**
 * Attempt to pull the closest matching location out of the job.
 */
export const getJobTileLocation = (props: GetJobTileLocationProps): string[] => {
    const {
        inputDMA,
        remote_work_location_type,
        locations,
        userLocationDma
    } = props;
    const isRemote: boolean = remote_work_location_type === "virtual";
    // We always prefer the dropdown if it was used.
    const userLocation: string = inputDMA || userLocationDma;
    // We Only care about the actual location address and fallback to country.
    // eslint-disable-next-line max-len
    const locationStrings: string[] = locations.map((x) => (!(/\w/).test(x.address) ? (x.country as undefined as string).toUpperCase() : x.address));
    // This is the fully matched location (city and state).
    const matchedLocation: string = locationStrings.find((loc) => loc === userLocation);
    // We may need to match only on the last segment of the location.
    const getState = (txt: string) => {
        if (typeof txt === "string") {
            return txt.split(",")[1]?.trim()?.toLowerCase();
        }
        return null;
    };
    // First location matched by region.
    const matchedRegion: string = locationStrings.find((address) => getState(address) === getState(userLocation));
    // First location in the list (fall back)
    const firstLocation: string = locationStrings[0];
    // The location to display
    const displayLocation: string = matchedLocation || matchedRegion || firstLocation || "";
    // If we have more than 2 locations pluralize the string.
    const locationWord: string = ` location${locationStrings.length > 2 ? "s" : ""}`;
    // If we have more than 1 location we add a plus sign and the number plus the word location(s)
    const extraLocations: string = locationStrings.length > 1 ? ` + ${locationStrings.length - 1}${locationWord}` : "";
    // If we are remote and there is a location we will separate them with a slash.
    const separator: string = displayLocation ? " / " : "";
    // Build the remote string.
    const remoteString: string = isRemote ? `${separator}Remote` : "";

    // all together now...
    return [`${displayLocation}${remoteString}${extraLocations}`];
};

interface GetTrackingProps {
    job: JobSearchHit;
    viewJobHref: string;
    total: number;
    index: number;
    searchId: string;
}

/**
 * Generates the tracking props for a card.
 * Returns an object.
 */
export const getTrackingProps = (props: GetTrackingProps): Record<string, any> => {
    const {
        job,
        viewJobHref,
        total,
        index,
        searchId
    } = props;
    return {
        ...genJobTrackingProps(job),
        link_href: viewJobHref,
        total_results_on_page: total,
        placement: "search_results",
        order_num: index + 1,
        sp_tile_order: index + 1,
        sp_tile_search_id: searchId,
        sp_impression_search_id: searchId,
    };
};

interface EventData {
    searchId: string;
    companyLegacyId: string;
    legacy_id: string;
    snowplow_id: string;
    index: number;
}

/**
 * Generates Snowplow event base data for the various job tile click events.
 */
export const getSnowplowEventData = (eventData: EventData) => {
    const {
        searchId,
        companyLegacyId,
        legacy_id,
        snowplow_id,
        index,
    } = eventData;
    return {
        sp_click_component: "button",
        sp_click_component_tile_type: "job",
        sp_click_button_type: null,
        sp_click_position: "main",
        sp_click_target_page_section: "job_detail",
        sp_click_search_id: searchId,
        sp_tile_type: "job",
        sp_tile_company_id: companyLegacyId?.toString() ?? "",
        sp_tile_entity_id: legacy_id?.toString() ?? "",
        sp_tile_entity_uuid: snowplow_id,
        sp_tile_location: null,
        sp_tile_order: index + 1
    };
};

/**
 * Renders an SVG component representing a placeholder or null logo.
 */
export const NullLogo = () => (
    <svg
        className={styles.nullLogo}
        width="29"
        height="37"
        viewBox="0 0 29 37"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <g clipPath="url(#clip0_611_2508)">
            <path
                d="M28.5 35.0277C28.5 35.2929 28.3946 35.5473 28.2071 35.7348C28.0196 35.9223
                27.7652 36.0277 27.5 36.0277H14.5V1.9717C14.4999 1.78781 14.5504 1.60745 14.6461
                1.45043C14.7418 1.29341 14.879 1.16582 15.0425 1.08169C15.206 0.997558 15.3895
                0.96014 15.5729 0.973549C15.7563 0.986959 15.9325 1.05068 16.082 1.1577L28.082
                9.7297C28.2114 9.82232 28.3169 9.94451 28.3896 10.0861C28.4623 10.2277 28.5001
                10.3845 28.5 10.5437V35.0277Z"
                stroke="#767676"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path d="M26.4995 8.60008V3.02808" stroke="#767676" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M14.5 10.0282H18.5" stroke="#767676" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M14.5 16.028H20.5" stroke="#767676" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M0.499756 22.028H6.49976" stroke="#767676" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M0.499756 28.0281H6.49976" stroke="#767676" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M14.5 22.028H23.5" stroke="#767676" strokeLinecap="round" strokeLinejoin="round" />
            <path
                d="M18.5002 36.0281V29.0281C18.5002 28.7629 18.6056 28.5085 18.7931 28.321C18.9807
                28.1334 19.235 28.0281 19.5002 28.0281H23.5002C23.7655 28.0281 24.0198 28.1334 24.2074
                28.321C24.3949 28.5085 24.5002 28.7629 24.5002 29.0281V36.0281"
                stroke="#767676"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M14.4998 16.028H1.49976C1.23454 16.028 0.980185 16.1333 0.792649 16.3208C0.605113 16.5084
                0.499756 16.7627 0.499756 17.028V35.028C0.499756 35.2932 0.605113 35.5475 0.792649 35.7351C0.980185
                35.9226 1.23454 36.028 1.49976 36.028H14.4998"
                stroke="#767676"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </g>
        <defs>
            <clipPath id="clip0_611_2508">
                <rect width="29" height="37" fill="white" />
            </clipPath>
        </defs>
    </svg>
);
NullLogo.displayName = "NullLogo";

interface JobTileProps {
    job: JobSearchHit;
    index: number;
    total: number;
    inputDMA: string;
    userLocationDma: string;
    viewed?: boolean;
    setSelectedJob: React.Dispatch<React.SetStateAction<JobSearchHit>>;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * JobTileMinimal is similar to the JobTile but has fewer buttons and very different triggers.
 */
export const JobTileMinimal: React.FC<JobTileProps> = ({
    job,
    total,
    inputDMA,
    userLocationDma,
    viewed,
    index,
    setSelectedJob,
    setIsOpen,
}) => {
    const searchDispatch = useSearchDispatch();
    const { searchId } = useSearchState();
    const {
        company: {
            legacy_id: companyLegacyId,
            logo,
            name,
            short_name,
            pkg,
            client_tier
        },
        id,
        legacy_id,
        locations,
        postedDate,
        remote_work_location_type,
        short_title,
        snowplow_id,
        title,
    } = job.hit as undefined as Record<string, any>;
    const [imageError, setImageError] = useState<boolean>(false);

    const companyHref = `/profiles/${short_name}`;
    const viewJobHref = `/jobs/${short_name}/${short_title}`;
    const ctaLabel = `View ${title}. This opens in a new tab.`;

    // a list of rules for using the fallback logo
    const logoRules = [
        // Some payment tiers do not have logos or profiles.
        ["archived", "lite"].includes(client_tier),
        // Programmatic companies do not have logos or profiles.
        pkg === "Programmatic",
        // Sometimes we have not yet received the companies logo.
        !logo,
        // An error loading the logo.
        imageError
    ];
    const logoImage = logoRules.some(Boolean) ? null : logo;

    /**
     * Generates the tracking properties for a job tile.
     */
    const trackingProps = getTrackingProps({
        job, viewJobHref, total, index, searchId
    });

    /**
     * Generates the location of a job tile based on various location parameters.
     */
    const tileLocation = getJobTileLocation({
        inputDMA,
        remote_work_location_type,
        locations,
        userLocationDma
    });

    /**
     * Generates the Snowplow event data for tracking purposes.
     */
    const eventData = getSnowplowEventData({
        searchId,
        companyLegacyId,
        legacy_id,
        snowplow_id: snowplow_id || id,
        index,
    });

    /**
     * Handles the click event on the entire job tile.
     *
     * Stops the event from propagating and prevents the default action.
     * Triggers a Snowplow click event with specific tracking data.
     * Sets the active job.
     * Shows the job description iframe.
     */
    const handleTileClick = (event: React.SyntheticEvent<HTMLElement>) => {
        event.stopPropagation();
        event.preventDefault();
        triggerSnowplowClickEvent({
            ...eventData,
            sp_click_component: "tile"
        });
        setSelectedJob(job);
        setIsOpen(true);
    };

    /**
     * Handles the click event on a view job link within a job tile.
     *
     * Stops the event from propagating and prevents the default action.
     * Triggers a Snowplow click event with specific tracking data.
     * Opens the job description page in a new browser tab.
     */
    const handleViewClick = (event: React.SyntheticEvent<HTMLElement>) => {
        event.stopPropagation();
        event.preventDefault();
        triggerSnowplowClickEvent({
            ...eventData,
            sp_click_component: "button",
            sp_click_button_type: "job_view",
            sp_click_button_text: "View Job >",
        });
        window.open(viewJobHref, "_blank");
    };

    /**
     * Handles the click event on a company name link within a job tile.
     *
     * Stops the event from propagating and prevents the default action.
     * Triggers a Snowplow click event with specific tracking data.
     * Opens the company's profile page in a new browser tab.
     */
    const handleCompanyClick = (event: React.SyntheticEvent<HTMLElement>) => {
        event.stopPropagation();
        event.preventDefault();
        triggerSnowplowClickEvent({
            ...eventData,
            sp_click_component: "tile",
            sp_click_button_type: "profile",
            sp_click_button_text: name,
            sp_click_target_page_section: "profile",
        });
        window.open(companyHref, "_blank");
    };

    return (
        <TrackedElement
            key={`tracked-${id}`}
            eventName="Job Tile Viewed"
            eventProps={trackingProps}
            enabled={!viewed}
            callback={() => searchDispatch({
                type: UserInteractionActionTypes.updateJobTileViews,
                payload: { tilesViewedJobIds: [id] }
            })}
        >
            <div
                className={styles.jobTile}
                onClick={handleTileClick}
                role="presentation"
            >
                <div className={styles.logo}>
                    {logoImage
                        ? (
                            <img
                                src={logoImage}
                                alt=""
                                width="100"
                                height="100"
                                loading="eager"
                                onError={() => setImageError(true)}
                            />
                        )
                        : (<NullLogo />)}
                </div>
                <div className={styles.body}>
                    <h2 className={styles.title}>{title}</h2>
                    <div className={styles.details}>
                        At
                        {" "}
                        {pkg === "Programmatic" ? (
                            <>{name}</>
                        ) : (
                            <a
                                href={companyHref}
                                onClick={handleCompanyClick}
                                onContextMenuCapture={handleCompanyClick}
                            >
                                {name}
                            </a>
                        )}
                        {" "}
                        -
                        {tileLocation}
                    </div>
                    <div className={styles.details}>
                        Posted on
                        {" "}
                        {postedDate}
                    </div>
                </div>
                <a
                    className={styles.cta}
                    href={viewJobHref}
                    aria-label={ctaLabel}
                    onClick={handleViewClick}
                    onContextMenuCapture={handleViewClick}
                >
                    <span>View Job</span>
                    <ArrowRightShape />
                </a>
            </div>
        </TrackedElement>
    );
};
JobTileMinimal.displayName = "JobTileMinimal";

export default JobTileMinimal;
